<template>
  <div>
    <RfPromoDetailsInfoRow title="Schedule">
      <template #promodetail>
        <div>
          <div v-if="item.sequence_id">
            Inherited from
            <router-link :to="getGuideLink(item.sequence_id, $route.params.aid)">{{
              item.sequence_name
            }}</router-link>
          </div>
          <div v-else style="padding-right: 30px">
            Starts on
            <span class="font-bold">{{
              dateDisplay(item.start_date, { utc: true, time: true })
            }}</span>
            and ends on
            <span class="font-bold">{{
              dateDisplay(item.end_date, { utc: true, time: true }) || "∞"
            }}</span
            >.
            <div class="pd-info-edit">
              <RfButton
                iconSize="16"
                title="edit"
                icon="md:edit"
                color="accent"
                size="x-small"
                variant="text"
                :disabled="editDisabled"
                @click="showPromoDetailDialog('schedule')"
              />
            </div>
          </div>
        </div>
      </template>
    </RfPromoDetailsInfoRow>
    <RfPromoDetailsInfoRow title="Daypart">
      <template #promodetail>
        <div>
          <div v-if="item.sequence_id">
            Inherited from
            <router-link :to="getGuideLink(item.sequence_id, $route.params.aid)">{{
              item.sequence_name
            }}</router-link>
          </div>
          <div v-else>
            <div v-if="!item.filter.schedule.length">-</div>
            <div v-else style="padding-right: 30px" class="pd-schedule-list">
              <div v-for="(scheduleItem, index) in item.filter.schedule" :key="index">
                {{ WeekDay[scheduleItem[0]] }}, {{ StringUtils.hhmmDisplay(scheduleItem[1]) }} -
                {{ StringUtils.hhmmDisplay(scheduleItem[2]) }}
              </div>
            </div>
            <div class="pd-info-edit">
              <RfButton
                iconSize="16"
                title="edit"
                icon="md:edit"
                color="accent"
                size="x-small"
                variant="text"
                :disabled="editDisabled"
                @click="showPromoDetailDialog('daypart')"
              />
            </div>
          </div>
        </div>
      </template>
    </RfPromoDetailsInfoRow>
    <RfPromoDetailsInfoRow v-if="showEngagementCategoryField" title="Engagement Category">
      <template #promodetail>
        <div class="flex items-center gap-2 p-0!">
          <div v-if="item.engagement_category" class="flex items-center gap-2">
            <RfColorTag
              :backgroundColor="getCategoryColor(item.engagement_category)"
              :title="getCategoryLabel(item.engagement_category)"
              pill
              disabled
              size="small"
              class="p-0! *:py-1!"
            />
            <span
              v-if="item.engagement_subcategory"
              class="text-caption text-grey-2"
              :title="subcategoryDescription(item.engagement_subcategory) || getSubcategoryLabel(item.engagement_subcategory)"
            >
              {{ getSubcategoryLabel(item.engagement_subcategory) }}
            </span>
            <span v-if="isInherited" class="text-body text-beige-50">(inherited from guide)</span>
          </div>
          <div v-else class="pr-7.5">-</div>
          <div v-if="!isInherited" class="pd-info-edit">
            <RfButton
              iconSize="16"
              title="edit"
              icon="md:edit"
              color="accent"
              size="x-small"
              variant="text"
              :disabled="editDisabled"
              @click="emit('editCategory')"
            />
          </div>
        </div>
      </template>
    </RfPromoDetailsInfoRow>
    <RfBaseModal
      ref="scheduleModal"
      title="When should it be shown"
      cancelText="Close"
      confirmText="Save"
      @primary="submitConfiguration"
    >
      <template #body>
        <div class="flex flex-col gap-3">
          <RfButtonTabs :active="scheduleType">
            <RfButtonNew
              v-for="(name, i) in scheduleTabs"
              :key="name"
              type="plain"
              size="large"
              class="[&_.rf-button--container]:text-beige-90 px-4 py-2 capitalize"
              :class="{
                '[&_.rf-button--container]:font-semibold!': i === scheduleType,
                'hidden!': scheduleTabs.length === 1,
              }"
              @click="scheduleType = i"
            >
              {{ name }}
            </RfButtonNew>
          </RfButtonTabs>
          <RfSchedule ref="schedule" :model="item" :scheduleType="scheduleTabs[scheduleType]" />
        </div>
      </template>
    </RfBaseModal>
  </div>
</template>

<script setup>
import RfPromoDetailsInfoRow from "@/components/RfPromotions/RfPromoDetailsInfoRow.vue";
import RfSchedule from "@/components/RfPathCreate/RfSchedule.vue";
import RfButton from "@/components/buttons/RfButton.vue";
import StringUtils from "@/utils/StringUtils";
import { cloneDeep } from "lodash-es";
import { dateDisplay } from "@/utils/DateDisplayUtils";
import { getGuideLink } from "@/utils/getLink";
import { useToastsStore } from "@/pinia/toastsStore";
import { useSequencesStore } from "@/pinia/sequencesStore";
import { usePromptStore } from "@/pinia/promptStore";
import { useEngagementCategories } from "@/utils/composables/useEngagementCategories";
import { ref, computed, useTemplateRef } from "vue";
import RfBaseModal from "@/components/modals/RfBaseModal.vue";
import RfColorTag from "@/components/tags/RfColorTag.vue";
import { getCategoryColor, getCategoryLabel, getSubcategoryLabel } from "@/utils/constants/EngagementCategoryConstants";
import { useEngagementSubcategories } from "@/utils/composables/useEngagementSubcategories";
import RfButtonTabs from "@/components/buttons/RfButtonTabs.vue";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import { useRoute } from "vue-router";

const scheduleTabs = ["schedule", "daypart"];
const WeekDay = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const emit = defineEmits(["editCategory"]);
const toastsStore = useToastsStore();
const sequencesStore = useSequencesStore();
const promptStore = usePromptStore();
const route = useRoute();

const props = defineProps({
  item: { type: Object, default: () => ({}) },
  editDisabled: { type: Boolean, default: false },
});

const scheduleModal = useTemplateRef("scheduleModal");
const schedule = useTemplateRef("schedule");
const scheduleType = ref(0);

const { engagementCategoriesEnabled: showEngagementCategoryField } = useEngagementCategories();
const { subcategoryDescription } = useEngagementSubcategories();
const isInherited = computed(() => !!props.item?.sequence_id);

const showPromoDetailDialog = type => {
  scheduleModal.value?.show();
  scheduleType.value = type === "daypart" ? 1 : 0;
};

const submitConfiguration = async () => {
  try {
    const appId = route.params.aid;
    const newPath = cloneDeep(props.item);
    schedule.value?.fillModel(newPath);
    let title = "Prompt";
    if (props.item.subject_name === "Sequence") {
      title = "Guide";
      await sequencesStore.updateSequence({
        appId,
        sequenceId: route.params.sid,
        modelSequence: newPath,
      });
    } else {
      await promptStore.updatePrompt({
        appId,
        pathId: route.params.pid,
        modelPath: newPath,
      });
    }
    scheduleModal.value?.close();
    toastsStore.create({ type: "success", body: `${title} updated successfully` });
  } catch (e) {
    toastsStore.create({ type: "error", body: e.message });
  }
};
</script>
